import React, { useState, useEffect, useRef } from 'react';
import { DeliveryHistory, DeliverySchedule, Subscription, Address, DeliveryStatus } from '../types/api';
import { format, isFuture, parseISO } from 'date-fns';
import { ArrowRight, Calendar } from 'lucide-react';
import { deliveryService } from '../services';
import { subscriptionService } from '../services';
import { useNavigate } from 'react-router-dom';
import useBottomNavPadding from '../hooks/useBottomNavPadding';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';

interface DriverLocation {
  lat: number;
  lng: number;
  updatedAt: string;
}

const formatAddress = (address: Address, t: any) => {
  const parts = [
    address.street,
    address.city,
    address.state,
    address.zipCode,
    address.country || t('deliveryActivity.defaultCountry')
  ].filter(Boolean);

  // Split into two lines
  const midPoint = Math.ceil(parts.length / 2);
  const line1 = parts.slice(0, midPoint).join(', ');
  const line2 = parts.slice(midPoint).join(', ');

  return { line1, line2 };
};

const getStatusDisplay = (status: DeliveryStatus, t: any) => {
  switch (status) {
    case 'delivered':
      return t('deliveryActivity.status.delivered');
    case 'scheduled':
      return t('deliveryActivity.status.scheduled');
    case 'skipped':
      return t('deliveryActivity.status.skipped');
    default:
      return t('deliveryActivity.status.scheduled'); // Default to scheduled for safety
  }
};


export default function DeliveryActivity() {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState<DeliveryHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<{[subscriptionId: string]: { line1: string; line2: string }}>({});
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryHistory | null>(null);
  const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(null);
  const [driverId, setDriverId] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Map references
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  // Apply bottom padding for the navigation
  useBottomNavPadding();

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) return;
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const fetchDeliveryData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Fetching delivery data...");
      
      // Get all user deliveries from all subscriptions
      const allDeliveries = await deliveryService.getAllUserDeliveries();
      console.log(`Retrieved ${allDeliveries.length} deliveries`);
      
      // Process addresses from delivery data
      const addressMap: {[subscriptionId: string]: { line1: string; line2: string }} = {};
      
      allDeliveries.forEach(delivery => {
        if (delivery.subscriptionId && delivery.address) {
          addressMap[delivery.subscriptionId] = formatAddress(delivery.address, t);
        }
      });
      
      setAddresses(addressMap);
      
      // Filter and limit to 5 most recent deliveries for display
      const limitedDeliveries = allDeliveries
        .filter(delivery => delivery.status !== 'skipped')
        .slice(0, 5);
        
      setDeliveries(limitedDeliveries);
      
      // Set the most recent active delivery as selected for map display
      const activeDelivery = limitedDeliveries.find(d => d.status === 'scheduled');
      if (activeDelivery) {
        setSelectedDelivery(activeDelivery);
        // If there's driver info in the delivery data, set it
        if (activeDelivery.driver_id) {
          setDriverId(activeDelivery.driver_id);
        }
      }
    } catch (error) {
      console.error('Error fetching delivery data:', error);
      setError(t('deliveryActivity.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  // Initialize map when driver location is available
  useEffect(() => {
    if (!window.google || !window.google.maps || !mapRef.current || !driverLocation) return;

    const mapOptions = {
      zoom: 15,
      center: { lat: driverLocation.lat, lng: driverLocation.lng },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    };

    // Create map instance
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
    } else {
      mapInstanceRef.current.setCenter({ lat: driverLocation.lat, lng: driverLocation.lng });
    }

    // Add driver marker with yellow tuk-tuk icon
    if (!markerRef.current) {
      // Create a custom marker with the emoji
      const label = {
        text: '🛺',
        fontSize: '24px'
      };

      markerRef.current = new window.google.maps.Marker({
        position: { lat: driverLocation.lat, lng: driverLocation.lng },
        map: mapInstanceRef.current,
        label: label,
        title: t('deliveryActivity.driverLocation')
      });
    } else {
      markerRef.current.setPosition({ lat: driverLocation.lat, lng: driverLocation.lng });
    }
  }, [driverLocation, t]);

  // Fetch driver location periodically
  useEffect(() => {
    if (!driverId) return;

    const fetchDriverLocation = async () => {
      try {
        const response = await apiClient.get(`/drivers/${driverId}/location`);
        if (response.data && response.data.data) {
          setDriverLocation({
            lat: response.data.data.current_location.lat,
            lng: response.data.data.current_location.lng,
            updatedAt: response.data.data.updated_at
          });
        }
      } catch (error) {
        console.error('Error fetching driver location:', error);
      }
    };

    // Fetch immediately
    fetchDriverLocation();

    // Then set up polling every 10 seconds
    const interval = setInterval(fetchDriverLocation, 10000);
    
    return () => clearInterval(interval);
  }, [driverId]);

  useEffect(() => {
    fetchDeliveryData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('deliveryActivity.loadingHistory')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-800 mb-4">{error}</p>
          <button 
            onClick={fetchDeliveryData}
            className="px-4 py-2 bg-amber-100 text-amber-800 rounded-full font-medium"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <main className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="flex items-top justify-between">
          <h1 className="text-3xl font-semibold mb-5">{t('deliveryActivity.pageTitle')}</h1>
          <div className="relative" onClick={() => navigate('/subscription-management')} style={{ cursor: 'pointer' }}>
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-50">
                  <Calendar size={20} className="text-amber-700" />
                </div>
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full border-2 border-white"></span>
              </div>
        </div>
        
        {/* Driver location map */}
        {driverId ? (
          <div className="mb-6 rounded-lg overflow-hidden shadow-sm">
            <div 
              ref={mapRef} 
              className="w-full h-[200px]"
              style={{ 
                background: driverLocation ? 'transparent' : '#f3f4f6', 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {!driverLocation && 
                <div className="text-gray-500">
                  {t('deliveryActivity.loadingDriverLocation')}
                </div>
              }
            </div>
            {selectedDelivery && (
              <div className="bg-white p-3 text-sm">
                <div className="flex items-center">
                  <span className="text-amber-600 mr-2">🛺</span>
                  <p>
                    {driverLocation ? t('deliveryActivity.driverOnWay') : t('deliveryActivity.trackingDriver')}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
        
        <h2 className="font-medium mb-4">{t('deliveryActivity.recentSection')}</h2>
        
        <div className="space-y-4">
          {deliveries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>{t('deliveryActivity.noDeliveryHistory')}</p>
            </div>
          ) : (
            deliveries.map((delivery) => (
              <div 
                key={delivery.id}
                className="pb-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/delivery/${delivery.id}`)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium">
                      {getStatusDisplay(delivery.status, t)} - {format(new Date(delivery.date), 'dd MMM yyyy')}
                    </h3>
                    {delivery.subscriptionId && delivery.subscriptionName && (
                      <p className="text-sm text-amber-700 mt-1">
                        {delivery.subscriptionName}
                      </p>
                    )}
                    {delivery.subscriptionId && addresses[delivery.subscriptionId] && (
                      <p className="text-gray-500 text-sm mt-1">
                        {addresses[delivery.subscriptionId].line1}<br />
                        {/* {addresses[delivery.subscriptionId].line2} */}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-4 mt-3">
                  {delivery.status === 'delivered' && (
                    <button className="flex items-center text-[#8B4513] font-medium border-none p-0 bg-transparent">
                      <span className="flex items-center bg-amber-100 px-3 py-1.5 rounded-full">
                        {t('deliveryActivity.actions.rate')} <ArrowRight className="ml-1 w-4 h-4" />
                      </span>
                    </button>
                  )}
                  <button className="flex items-center text-[#8B4513] font-medium border-none p-0 bg-transparent">
                    <span className="flex items-center bg-amber-100 px-3 py-1.5 rounded-full">
                      {t('deliveryActivity.actions.details')} <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </main>
  );
}